import React, { useMemo, useCallback, useContext, useState } from 'react';
import Context from "./Context";
import { nodes as staticNodeDefinitions } from "./nodes";
import Icons from "./Icons"; // Map chứa icon của các node
import { timDinhNghiaNode } from "./nodeSearch";

// --- MUI Components & Icons ---
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    Stack,
    Chip,
    CircularProgress,
    TextField,
    InputAdornment,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/** Dữ liệu mặc định khi thả node xuống sơ đồ — lấy từ metadata.default. */
const taoDuLieuMacDinh = (node) => {
    const defaultData = { name: node.label || node.type };
    (node.metadata || []).forEach((m) => {
        if (m.default !== undefined) defaultData[m.name] = m.default;
    });
    return defaultData;
};

const moTaTooltip = (node) => ((node.description || node.inputDescription || node.outputDescription) ? (
    <Box sx={{ maxWidth: 320 }}>
        {node.__dynamic && <div style={{ marginBottom: 6, fontStyle: 'italic' }}>Node do máy chủ cung cấp (client chưa có giao diện riêng) — vẫn cấu hình được bằng các trường bên dưới.</div>}
        {node.description && <div style={{ marginBottom: 6 }}>{node.description}</div>}
        {node.inputDescription && <div style={{ marginBottom: 4 }}><b>Đầu vào:</b> {node.inputDescription}</div>}
        {node.outputDescription && <div><b>Đầu ra:</b> {typeof node.outputDescription === 'string' ? node.outputDescription : JSON.stringify(node.outputDescription)}</div>}
    </Box>
) : "");

/** Một node trong danh sách — kéo được vào sơ đồ. `showUsage`: hiện 1 dòng công dụng (khi đang tìm). */
const NodeItem = ({ node, onDragStart, showUsage, badge }) => {
    const nodeIcon = Icons[node.type] || Icons.default;
    const coMoTa = !!(node.description || node.inputDescription || node.outputDescription);
    return (
        <Tooltip
            title={moTaTooltip(node)}
            placement="right"
            arrow
            disableHoverListener={!coMoTa}
            enterDelay={400}
            slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [0, 12] } }] } }}
        >
            <Box
                draggable
                onDragStart={(event) => onDragStart(event, node.type, taoDuLieuMacDinh(node))}
                className="drag-item node-item"
                sx={{
                    display: 'flex',
                    alignItems: showUsage ? 'flex-start' : 'center',
                    px: 1,
                    py: 0.6,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    cursor: 'grab',
                    bgcolor: 'background.paper',
                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                    '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                    },
                    '&:active': { cursor: 'grabbing' },
                    ...node.style // Giữ lại style tùy chỉnh từ định nghĩa node nếu có
                }}
            >
                <Box sx={{ display: 'inline-flex', mr: 1, width: 16, justifyContent: 'center', pt: showUsage ? '3px' : 0, fontSize: 13 }}>
                    <FontAwesomeIcon icon={nodeIcon} className="node-icon" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, userSelect: 'none', fontSize: 13, lineHeight: 1.35 }} noWrap>
                        {node.label}
                    </Typography>
                    {showUsage && node.description && (
                        <Typography variant="caption" color="text.secondary"
                            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.3, fontSize: 11, userSelect: 'none' }}>
                            {node.description}
                        </Typography>
                    )}
                </Box>
                {badge}
                {node.__dynamic && (
                    <Chip label="động" size="small" variant="outlined"
                        sx={{ height: 16, fontSize: '0.6rem', ml: 0.5, '& .MuiChip-label': { px: 0.6 } }} />
                )}
            </Box>
        </Tooltip>
    );
};

/**
 * Sidebar — danh sách node để kéo vào sơ đồ.
 * Có ô tìm theo TÊN và theo CÔNG DỤNG (mô tả, đầu vào/đầu ra, nhóm), không phân biệt dấu.
 * `onCollapse`: nếu có, hiện nút thu gọn thanh bên để nhường chỗ cho sơ đồ.
 */
const Sidebar = ({ onCollapse, width = 210 }) => {
    const context = useContext(Context) || {};

    // Danh sách node lấy từ FlowEditor — đã ĐỒNG BỘ với máy chủ (node của gói chưa cài không có mặt
    // ở đây nữa). Dùng bản tĩnh làm dự phòng cho mã cũ đặt Sidebar ngoài FlowEditor.
    const nodeDefinitions = context.nodeDefinitions || staticNodeDefinitions;
    const loading = !!context.nodesLoading;

    const [q, setQ] = useState('');
    const [cheDo, setCheDo] = useState('all'); // 'all' | 'name' | 'usage'
    const dangTim = q.trim().length > 0;

    // Nhóm các node theo category
    const groupedNodes = useMemo(() => {
        return (nodeDefinitions || []).reduce((acc, node) => {
            const category = node.category || 'Khác';
            if (!acc[category]) acc[category] = [];
            acc[category].push(node);
            return acc;
        }, {});
    }, [nodeDefinitions]);

    const ketQua = useMemo(
        () => (dangTim ? timDinhNghiaNode(nodeDefinitions, q, cheDo) : []),
        [dangTim, nodeDefinitions, q, cheDo]
    );

    // Hàm xử lý bắt đầu kéo thả
    const onDragStart = useCallback((event, nodeType, nodeData) => {
        const dataString = JSON.stringify({ data: nodeData, type: nodeType });
        event.dataTransfer.setData('application/reactflow', dataString);
        event.dataTransfer.effectAllowed = 'move';
    }, []);

    return (
        <Box
            className="sidebar-container"
            sx={{
                width,
                flexShrink: 0,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRight: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper'
            }}
        >
            {/* Header: tiêu đề + ô tìm kiếm */}
            <Box sx={{ px: 1, pt: 1, pb: 0.75, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', flexShrink: 0 }}>
                <Stack direction="row" alignItems="center" sx={{ mb: 0.75, pl: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', flex: 1, fontSize: 13 }}>
                        Kéo node vào sơ đồ
                    </Typography>
                    {loading && <CircularProgress size={12} thickness={5} sx={{ mr: 0.5 }} />}
                    {onCollapse && (
                        <Tooltip title="Thu gọn thanh node">
                            <IconButton size="small" onClick={onCollapse} sx={{ p: '2px' }}>
                                <KeyboardDoubleArrowLeftRoundedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
                <TextField
                    size="small"
                    fullWidth
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setQ(''); }}
                    placeholder="Tìm node…"
                    slotProps={{
                        input: {
                            sx: { fontSize: 13, bgcolor: 'background.paper', '& input': { py: '6px' } },
                            startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                            endAdornment: q ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setQ('')} sx={{ p: '2px' }}>
                                        <CloseRoundedIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }
                    }}
                />
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    fullWidth
                    value={cheDo}
                    onChange={(_, v) => v && setCheDo(v)}
                    sx={{ mt: 0.75, '& .MuiToggleButton-root': { py: '1px', fontSize: 11, textTransform: 'none', fontWeight: 600 } }}
                >
                    <ToggleButton value="all">Tất cả</ToggleButton>
                    <ToggleButton value="name">Theo tên</ToggleButton>
                    <ToggleButton value="usage">Công dụng</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Nội dung (cuộn) */}
            <Box className="sidebar-content" sx={{ flexGrow: 1, overflowY: 'auto' }}>
                {dangTim ? (
                    <Box sx={{ p: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 0.5, pb: 0.5 }}>
                            {ketQua.length ? `${ketQua.length} node khớp` : 'Không có node nào khớp'}
                        </Typography>
                        <Stack spacing={0.75}>
                            {ketQua.map(({ def, by }) => (
                                <NodeItem
                                    key={def.type}
                                    node={def}
                                    onDragStart={onDragStart}
                                    showUsage={by === 'usage' || cheDo === 'usage'}
                                />
                            ))}
                        </Stack>
                    </Box>
                ) : (
                    Object.entries(groupedNodes).map(([category, nodesInCategory]) => (
                        <Accordion
                            disableGutters
                            elevation={0}
                            defaultExpanded={false} // mặc định thu gọn từng nhóm cho gọn; bấm tên nhóm để mở
                            key={category}
                            slotProps={{ transition: { unmountOnExit: true } }}
                            sx={{
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:before': { display: 'none' } // Loại bỏ đường line mặc định của MUI Accordion
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon fontSize="small" />}
                                sx={{ minHeight: 32, px: 1, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>
                                    {category}
                                    <Box component="span" sx={{ ml: 0.5, fontWeight: 500, color: 'text.disabled' }}>({nodesInCategory.length})</Box>
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 0.75, pt: 0, pb: 0.75 }}>
                                <Stack spacing={0.5}>
                                    {nodesInCategory.map((node) => (
                                        <NodeItem key={node.type} node={node} onDragStart={onDragStart} />
                                    ))}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    ))
                )}
            </Box>
        </Box>
    );
};

export default Sidebar;
